import { useState } from "react";
import { toast } from "sonner";
import { Paperclip, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import AttachmentPicker from "../feedback/AttachmentPicker";
import { api, formatApiError } from "../../lib/api";
import { openAttachment } from "../../lib/files";
import { fmtDate } from "../../lib/labels";

const Files = ({ items, base }) => items?.length > 0 && (
  <div className="mt-2 flex flex-wrap gap-2">{items.map((a) => (
    <button key={a.id} onClick={() => openAttachment(`${base}/${a.id}`)} className="flex items-center gap-1 rounded-md bg-white/70 px-2 py-1 text-xs underline" data-testid={`ticket-attachment-${a.id}`}><Paperclip className="h-3 w-3" />{a.filename}</button>))}</div>
);

export const Messages = ({ ticket, base, viewer }) => (
  <div className="space-y-3" data-testid="ticket-messages">
    <div className="rounded-lg border bg-slate-50 p-4 text-sm"><p className="text-xs text-slate-500">{ticket.requester_name} · {fmtDate(ticket.created_at)}</p>
      <p className="mt-1 whitespace-pre-wrap">{ticket.description}</p><Files items={ticket.attachments} base={base} /></div>
    {ticket.messages.map((m) => (
      <div key={m.id} data-testid={m.internal ? "ticket-internal-note" : "ticket-message"}
        className={`rounded-lg p-4 text-sm ${m.internal ? "border border-dashed border-amber-300 bg-amber-50" : m.side === "support" ? (viewer === "support" ? "ml-8 bg-indigo-50" : "mr-8 bg-indigo-50") : (viewer === "support" ? "mr-8 bg-white border" : "ml-8 bg-white border")}`}>
        <p className="flex items-center gap-1 text-xs text-slate-500">{m.internal && <Lock className="h-3 w-3" />}{m.internal ? "Note interne · " : ""}{m.side === "support" ? `Support — ${m.author_name}` : m.author_name} · {fmtDate(m.created_at)}</p>
        <p className="mt-1 whitespace-pre-wrap">{m.body}</p><Files items={m.attachments} base={base} />
      </div>))}
  </div>
);

export const ReplyBox = ({ url, allowInternal, statusOptions, onDone, disabled }) => {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [internal, setInternal] = useState(false);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) { toast.error("Message vide"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("payload", JSON.stringify({ body, ...(allowInternal ? { internal, status: internal ? null : status || null } : {}) }));
    files.forEach((x) => fd.append("files", x));
    try { const { data } = await api.post(url, fd); setBody(""); setFiles([]); setStatus(""); toast.success(internal ? "Note interne ajoutée" : "Message envoyé"); onDone(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (disabled) return <p className="text-sm text-slate-500" data-testid="ticket-reply-disabled">Ticket clôturé.</p>;
  return (
    <div className={`space-y-2 rounded-lg border p-3 ${internal ? "border-amber-300 bg-amber-50/50" : ""}`} data-testid="ticket-reply-box">
      <Textarea rows={3} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={internal ? "Note interne (jamais visible par le client)" : "Votre message"} data-testid="ticket-reply-input" />
      <AttachmentPicker files={files} onChange={setFiles} />
      <div className="flex flex-wrap items-center gap-3">
        {allowInternal && <label className="flex items-center gap-2 text-xs"><Switch checked={internal} onCheckedChange={setInternal} data-testid="ticket-internal-switch" />Note interne</label>}
        {allowInternal && !internal && <select className="field-select w-60" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-reply-status">
          <option value="">Statut après envoi : automatique</option>{statusOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
        <Button onClick={send} disabled={busy} data-testid="ticket-reply-send">{internal ? "Ajouter la note" : "Envoyer"}</Button>
      </div>
    </div>
  );
};
